import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MethodChoiceStep } from '../../components/mfa/MethodChoiceStep';
import { TotpQrCode } from '../../components/mfa/TotpQrCode';
import { BackupCodesDisplay } from '../../components/mfa/BackupCodesDisplay';
import { MfaCodeInput } from '../../components/auth/MfaCodeInput';
import { useAuth } from '../../contexts/AuthContext';
import { mfaApi, mfaSetupApi } from '../../lib/api';
import { BRAND } from '../../lib/theme';
import type { MfaMethod } from '../../types';

interface MfaSetupPageProps {
  dark?: boolean;
  onComplete: () => void;
  onLogout: () => void;
}

type WizardStep =
  | 'method'           // Step 1: choose email / sms / totp
  | 'totp_scan'        // Step 2a: scan QR + confirm code
  | 'sms_phone'        // Step 2b: enter phone number
  | 'sms_verify'       // Step 2c: enter SMS code
  | 'email_request'    // Step 2d: request email code
  | 'email_verify'     // Step 2e: enter email code
  | 'backup_codes';    // Step 3: show backup codes (TOTP only)

interface TotpSetupData {
  secretSetupKey: string;
  qrSvg: string;
  backupCodes: string[];
}

/**
 * Force-Setup Wizard — shown when mfa_setup_pending=true after login.
 *
 * The wizard is not cancellable (no "Skip" button). The only exit is Logout.
 * 3-4 steps depending on method chosen:
 *   email/sms:  Method → Enter credential → Verify code → done
 *   totp:       Method → QR + confirm code → Backup codes → done
 */
export function MfaSetupPage({ dark = false, onComplete, onLogout }: MfaSetupPageProps) {
  const { t } = useTranslation();
  const { mfaState, setChallengeId } = useAuth();

  const [step, setStep] = useState<WizardStep>('method');
  const [selectedMethod, setSelectedMethod] = useState<MfaMethod>('email');
  const [totpData, setTotpData] = useState<TotpSetupData | null>(null);
  const [phone, setPhone] = useState('');
  const [countryCode, setCountryCode] = useState('+49');
  const [challengeId, setLocalChallengeId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resetKey, setResetKey] = useState(0);

  // ── Stepper ────────────────────────────────────────────────────────────────

  const STEPS_BY_METHOD: Record<MfaMethod, WizardStep[]> = {
    email: ['method', 'email_request', 'email_verify'],
    sms:   ['method', 'sms_phone',    'sms_verify'],
    totp:  ['method', 'totp_scan',    'backup_codes'],
  };

  const stepList = STEPS_BY_METHOD[selectedMethod];
  const stepIndex = stepList.indexOf(step);
  const totalSteps = stepList.length;

  // ── Actions ────────────────────────────────────────────────────────────────

  const handleMethodSelected = async (method: MfaMethod) => {
    setSelectedMethod(method);
    setError('');

    if (method === 'totp') {
      setLoading(true);
      try {
        const data = await mfaSetupApi.setupTotp();
        setTotpData({
          secretSetupKey: data.secret_setup_key,
          qrSvg: data.qr_code_svg,
          backupCodes: [], // backup codes come from a separate /backup-codes/generate call (step-up gated)
        });
        setStep('totp_scan');
      } catch {
        setError(t('mfa.setup.step_confirm.invalid_code'));
      } finally {
        setLoading(false);
      }
    } else if (method === 'email') {
      setStep('email_request');
    } else {
      setStep('sms_phone');
    }
  };

  const handleEmailRequest = async () => {
    if (!mfaState?.userIdHint) return;
    setLoading(true);
    setError('');
    try {
      const result = await mfaApi.sendChallenge(mfaState.userIdHint, 'email');
      setLocalChallengeId(result.challenge_id);
      setChallengeId(result.challenge_id);
      setStep('email_verify');
    } catch {
      setError(t('mfa.challenge.send_failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleSmsPhoneSubmit = async () => {
    const mobile = `${countryCode}${phone}`.replace(/\s/g, '');
    if (!mobile || phone.length < 7) {
      setError(t('mfa.setup.step_sms.phone_error'));
      return;
    }
    setLoading(true);
    setError('');
    try {
      const result = await mfaSetupApi.setupSms(mobile);
      setLocalChallengeId(result.challenge_id);
      setStep('sms_verify');
    } catch {
      setError(t('mfa.challenge.send_failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleSmsVerify = async (code: string) => {
    if (!challengeId) return;
    setLoading(true);
    setError('');
    try {
      await mfaSetupApi.confirmSms(challengeId, code);
      onComplete();
    } catch {
      setError(t('mfa.setup.step_confirm.invalid_code'));
      setResetKey(k => k + 1);
    } finally {
      setLoading(false);
    }
  };

  const handleEmailVerify = async (code: string) => {
    if (!mfaState?.userIdHint) return;
    setLoading(true);
    setError('');
    try {
      await mfaApi.verifyChallenge({
        challenge_id: challengeId,
        code,
        user_id_hint: mfaState.userIdHint,
      });
      onComplete();
    } catch {
      setError(t('mfa.setup.step_confirm.invalid_code'));
      setResetKey(k => k + 1);
    } finally {
      setLoading(false);
    }
  };

  const handleTotpConfirm = async (code: string) => {
    setLoading(true);
    setError('');
    try {
      await mfaSetupApi.confirmTotp(code);
      // First-time TOTP setup → generate one-shot backup codes. Backend gates
      // this behind mfa.stepup; the step-up cookie from /auth/login is still
      // valid here (TTL 5 min, the wizard flow stays well within).
      try {
        const { backup_codes } = await mfaSetupApi.generateBackupCodes();
        setTotpData(prev => prev ? { ...prev, backupCodes: backup_codes } : prev);
      } catch {
        /* not fatal — user can regenerate later from settings */
      }
      setStep('backup_codes');
    } catch {
      setError(t('mfa.setup.step_confirm.invalid_code'));
    } finally {
      setLoading(false);
    }
  };

  const handleBackupDone = () => {
    onComplete();
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  const bg = dark ? '#0b101a' : '#f9fafb';
  const cardBg = dark ? '#141b27' : '#fff';
  const border = dark ? '#1e2735' : '#e4e7ec';
  const headingColor = dark ? '#f2f4f7' : '#101828';
  const mutedColor = dark ? '#8392a7' : '#667085';
  const textColor = dark ? '#cfd7e3' : '#344054';

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      minHeight: '100vh',
      alignItems: 'center',
      justifyContent: 'center',
      background: bg,
      padding: '24px 16px',
    }}>
      {/* Logout escape hatch */}
      <div style={{ position: 'absolute', top: 20, right: 24 }}>
        <button
          type="button"
          onClick={onLogout}
          style={{
            background: 'none',
            border: 'none',
            fontSize: 13,
            color: mutedColor,
            cursor: 'pointer',
          }}
        >
          {t('header.user_menu.logout')}
        </button>
      </div>

      <div style={{
        width: '100%',
        maxWidth: 480,
        background: cardBg,
        border: `1px solid ${border}`,
        borderRadius: 16,
        padding: 32,
        boxShadow: dark ? 'none' : '0 4px 24px rgba(16,24,40,0.08)',
      }}>
        {/* Stepper indicator */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 28 }}>
          {stepList.map((s, i) => (
            <div
              key={s}
              style={{
                flex: 1,
                height: 4,
                borderRadius: 3,
                background: i <= stepIndex ? BRAND : border,
                transition: 'background 0.2s',
              }}
            />
          ))}
        </div>
        <p style={{ fontSize: 11, color: mutedColor, marginBottom: 20 }}>
          {t('auth.register.step_indicator', { step: stepIndex + 1, total: totalSteps })}
        </p>

        {/* ── Step: Method Choice ── */}
        {step === 'method' && (
          <MethodChoiceStep
            selected={selectedMethod}
            onSelect={setSelectedMethod}
            onContinue={() => handleMethodSelected(selectedMethod)}
            forceSetup
          />
        )}

        {/* ── Step: TOTP scan + confirm ── */}
        {step === 'totp_scan' && totpData && (
          <div>
            <h2 style={{ fontSize: 20, fontWeight: 700, color: headingColor, marginBottom: 6 }}>
              {t('mfa.setup.step_totp.title')}
            </h2>
            <p style={{ fontSize: 13, color: mutedColor, marginBottom: 20 }}>
              {t('mfa.setup.step_totp.subtitle')}
            </p>
            {error && <ErrorBanner message={error} />}
            <TotpQrCode
              qrSvg={totpData.qrSvg}
              secretSetupKey={totpData.secretSetupKey}
              onCodeComplete={handleTotpConfirm}
              disabled={loading}
              error={!!error}
            />
            <BackButton onClick={() => setStep('method')} label={t('common.action.back')} />
          </div>
        )}

        {/* ── Step: SMS phone number entry ── */}
        {step === 'sms_phone' && (
          <div>
            <h2 style={{ fontSize: 20, fontWeight: 700, color: headingColor, marginBottom: 6 }}>
              {t('mfa.setup.step_sms.title')}
            </h2>
            {error && <ErrorBanner message={error} />}
            <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
              <select
                value={countryCode}
                onChange={e => setCountryCode(e.target.value)}
                aria-label={t('mfa.setup.step_sms.country_label')}
                style={{
                  width: 80,
                  padding: '9px 8px',
                  border: `1px solid ${border}`,
                  borderRadius: 9,
                  fontSize: 13,
                  background: dark ? '#0f1520' : '#fff',
                  color: textColor,
                }}
              >
                <option value="+49">+49 DE</option>
                <option value="+43">+43 AT</option>
                <option value="+48">+48 PL</option>
                <option value="+41">+41 CH</option>
              </select>
              <input
                type="tel"
                inputMode="tel"
                value={phone}
                onChange={e => setPhone(e.target.value.replace(/[^0-9]/g, ''))}
                placeholder={t('mfa.setup.step_sms.phone_hint')}
                aria-label={t('mfa.setup.step_sms.phone_label')}
                style={{
                  flex: 1,
                  padding: '9px 12px',
                  border: `1px solid ${border}`,
                  borderRadius: 9,
                  fontSize: 13,
                  background: dark ? '#0f1520' : '#fff',
                  color: textColor,
                }}
              />
            </div>
            <p style={{ fontSize: 11, color: mutedColor, marginBottom: 20 }}>
              {t('mfa.setup.step_sms.phone_hint')}
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <BackButton onClick={() => setStep('method')} label={t('common.action.back')} />
              <button
                type="button"
                onClick={handleSmsPhoneSubmit}
                disabled={loading || phone.length < 7}
                style={primaryBtnStyle(loading || phone.length < 7)}
              >
                {t('mfa.setup.step_sms.send_code')}
              </button>
            </div>
          </div>
        )}

        {/* ── Step: SMS / Email code verify ── */}
        {(step === 'sms_verify' || step === 'email_verify') && (
          <div>
            <h2 style={{ fontSize: 20, fontWeight: 700, color: headingColor, marginBottom: 6 }}>
              {t('mfa.setup.step_confirm.title')}
            </h2>
            <p style={{ fontSize: 13, color: mutedColor, marginBottom: 20 }}>
              {t('mfa.setup.step_confirm.subtitle')}
            </p>
            {error && <ErrorBanner message={error} />}
            <MfaCodeInput
              onComplete={step === 'sms_verify' ? handleSmsVerify : handleEmailVerify}
              disabled={loading}
              error={!!error}
              resetKey={resetKey}
            />
            <BackButton
              onClick={() => setStep(step === 'sms_verify' ? 'sms_phone' : 'method')}
              label={t('common.action.back')}
            />
          </div>
        )}

        {/* ── Step: Email request code ── */}
        {step === 'email_request' && (
          <div>
            <h2 style={{ fontSize: 20, fontWeight: 700, color: headingColor, marginBottom: 6 }}>
              {t('mfa.setup.step_email.title')}
            </h2>
            {error && <ErrorBanner message={error} />}
            <p style={{ fontSize: 14, color: mutedColor, marginBottom: 24 }}>
              {t('mfa.setup.step_email.target_label')}
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <BackButton onClick={() => setStep('method')} label={t('common.action.back')} />
              <button
                type="button"
                onClick={handleEmailRequest}
                disabled={loading}
                style={primaryBtnStyle(loading)}
              >
                {t('mfa.setup.step_email.request_button')}
              </button>
            </div>
          </div>
        )}

        {/* ── Step: Backup codes ── */}
        {step === 'backup_codes' && totpData && (
          <BackupCodesDisplay
            codes={totpData.backupCodes}
            onDone={handleBackupDone}
          />
        )}
      </div>
    </div>
  );
}

// ── Helper sub-components ────────────────────────────────────────────────────

function ErrorBanner({ message }: { message: string }) {
  return (
    <div style={{
      background: '#fef3f2',
      border: '1px solid #fecdca',
      borderRadius: 8,
      padding: '8px 12px',
      fontSize: 12,
      color: '#b42318',
      marginBottom: 16,
    }}>
      {message}
    </div>
  );
}

function BackButton({ onClick, label }: { onClick: () => void; label: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        background: 'none',
        border: '1px solid #e4e7ec',
        borderRadius: 9,
        padding: '9px 16px',
        fontSize: 13,
        color: '#344054',
        cursor: 'pointer',
        marginTop: 16,
      }}
    >
      ← {label}
    </button>
  );
}

function primaryBtnStyle(disabled: boolean): React.CSSProperties {
  return {
    background: disabled ? '#d0d5dd' : BRAND,
    color: '#fff',
    border: 'none',
    borderRadius: 9,
    padding: '10px 20px',
    fontSize: 14,
    fontWeight: 600,
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontFamily: 'Outfit, sans-serif',
    marginTop: 0,
  };
}
